import { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../../utils/api';
import { Button, Badge, Modal, Alert, SearchBar, Spinner, InfoRow, Pagination } from './shared';

const formatSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes >= 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return bytes + ' B';
};

const formatDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleDateString('vi-VN') + ' ' + d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
};

const DEFAULT_LIMIT = 50;

export default function AdminLongMemory() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [userIdFilter, setUserIdFilter] = useState('');
    const [offset, setOffset] = useState(0);
    const [limit, setLimit] = useState(DEFAULT_LIMIT);

    const loadData = useCallback(async (newOffset) => {
        const currentOffset = newOffset !== undefined ? newOffset : offset;
        setLoading(true);
        setError(null);
        try {
            const params = { limit, offset: currentOffset };
            if (userIdFilter.trim()) params.user_id = userIdFilter.trim();
            if (searchQuery.trim()) params.search = searchQuery.trim();
            const res = await adminApi.listLongMemoryItems(params);
            setItems(res.items || []);
            setTotal(res.total || 0);
        } catch (err) {
            setError(err.message || 'Failed to load store items');
        } finally {
            setLoading(false);
        }
    }, [userIdFilter, searchQuery, offset, limit]);

    useEffect(() => { loadData(0); setOffset(0); }, [limit]);
    useEffect(() => { loadData(); }, [offset]);

    const handleSearch = () => { setOffset(0); loadData(0); };

    const handlePageChange = (newOffset) => { setOffset(newOffset); };

    const handleLimitChange = (newLimit) => { setLimit(newLimit); setOffset(0); };

    const handleViewItem = async (prefix, key) => {
        try {
            const item = await adminApi.getLongMemoryItem(prefix, key);
            setSelectedItem(item);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDeleteItem = async (prefix, key) => {
        if (!confirm(`Delete "${key}" from "${prefix}"?`)) return;
        try {
            await adminApi.deleteLongMemoryItem(prefix, key);
            setSuccessMessage(`Deleted "${key}"`);
            setSelectedItem(null);
            setItems(prev => prev.filter(i => !(i.prefix === prefix && i.key === key)));
            setTotal(prev => Math.max(0, prev - 1));
            setTimeout(() => setSuccessMessage(null), 5000);
        } catch (err) {
            setError(err.message);
        }
    };

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div>
                    <h1 className="text-lg font-semibold text-gray-900">Long-term Memory</h1>
                    <p className="text-sm text-gray-500 mt-0.5">
                        {total} item{total !== 1 ? 's' : ''} in LangGraph Store
                    </p>
                </div>
            </div>

            {error && <Alert variant="error" onClose={() => setError(null)} className="mb-4">{error}</Alert>}
            {successMessage && <Alert variant="success" onClose={() => setSuccessMessage(null)} className="mb-4">{successMessage}</Alert>}

            <div className="flex flex-wrap items-center gap-2 mb-4">
                <SearchBar
                    value={searchQuery}
                    onChange={setSearchQuery}
                    onSearch={handleSearch}
                    onRefresh={() => loadData(offset)}
                    placeholder="Search key or content..."
                    className="flex-1"
                />
                <input
                    type="text"
                    value={userIdFilter}
                    onChange={(e) => setUserIdFilter(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder="User ID"
                    className="h-9 w-32 px-3 rounded-lg border border-gray-200 bg-white text-xs text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                />
            </div>

            {loading ? (
                <Spinner size="lg" text="Loading store..." center />
            ) : items.length === 0 ? (
                <div className="text-center py-12 text-gray-500 text-sm">No items found</div>
            ) : (
                <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50/60">
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 w-20">User</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 w-24">Namespace</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Key</th>
                                <th className="text-right px-3 py-2.5 text-xs font-medium text-gray-500 w-16">Size</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 w-36">Updated</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500 w-28">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {items.map((item) => (
                                <tr
                                    key={item.prefix + '::' + item.key}
                                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                                    onClick={() => handleViewItem(item.prefix, item.key)}
                                >
                                    <td className="px-4 py-2.5">
                                        {item.user_id
                                            ? <Badge variant="info">{item.user_id}</Badge>
                                            : <span className="text-xs text-gray-400">-</span>
                                        }
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <Badge variant="outline" className="text-[10px]">{item.namespace}</Badge>
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <span className="font-mono text-xs font-medium text-gray-900">{item.key}</span>
                                        {item.value_preview && (
                                            <p className="text-xs text-gray-500 truncate max-w-md mt-0.5">{item.value_preview}</p>
                                        )}
                                    </td>
                                    <td className="px-3 py-2.5 text-right">
                                        <span className="text-xs text-gray-500 font-mono">{formatSize(item.value_size)}</span>
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <span className="text-xs text-gray-500">{formatDate(item.updated_at)}</span>
                                    </td>
                                    <td className="px-4 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                                        <div className="flex items-center justify-end gap-1">
                                            <Button variant="ghost" size="xs" onClick={() => handleViewItem(item.prefix, item.key)}>View</Button>
                                            <Button
                                                variant="ghost" size="xs"
                                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                onClick={() => handleDeleteItem(item.prefix, item.key)}
                                            >
                                                Delete
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="px-4 pb-3">
                        <Pagination total={total} limit={limit} offset={offset} onChange={handlePageChange} onLimitChange={handleLimitChange} />
                    </div>
                </div>
            )}

            {selectedItem && (
                <ItemDetailModal
                    item={selectedItem}
                    onClose={() => setSelectedItem(null)}
                    onDelete={() => handleDeleteItem(selectedItem.prefix, selectedItem.key)}
                />
            )}
        </div>
    );
}

function ItemDetailModal({ item, onClose, onDelete }) {
    const content = item.value?.content || '';
    const hasContent = typeof content === 'string' && content.length > 0;
    const isListContent = Array.isArray(item.value?.content);

    return (
        <Modal open onClose={onClose} title={item.key} size="xl">
            <Modal.Body>
                <div className="grid grid-cols-2 gap-x-8 gap-y-0">
                    <InfoRow label="Prefix" value={item.prefix} mono />
                    <InfoRow label="User ID" value={item.user_id || '-'} />
                    <InfoRow label="Namespace" value={item.namespace} />
                    <InfoRow label="Key" value={item.key} mono />
                    <InfoRow label="Size" value={formatSize(item.value_size)} />
                    <InfoRow label="Created" value={formatDate(item.created_at)} />
                    <InfoRow label="Updated" value={formatDate(item.updated_at)} />
                </div>

                {isListContent ? (
                    <div className="mt-4">
                        <p className="text-sm font-medium text-gray-700 mb-2">Content</p>
                        <pre className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-xs font-mono text-gray-800 whitespace-pre-wrap overflow-y-auto max-h-[60vh]">
                            {item.value.content.join('\n')}
                        </pre>
                    </div>
                ) : hasContent ? (
                    <div className="mt-4">
                        <p className="text-sm font-medium text-gray-700 mb-2">Content</p>
                        <pre className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-xs font-mono text-gray-800 whitespace-pre-wrap overflow-y-auto max-h-[60vh]">
                            {content}
                        </pre>
                    </div>
                ) : (
                    <div className="mt-4">
                        <p className="text-sm font-medium text-gray-700 mb-2">Value (JSON)</p>
                        <pre className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-xs font-mono text-gray-800 whitespace-pre-wrap overflow-y-auto max-h-[60vh]">
                            {JSON.stringify(item.value, null, 2)}
                        </pre>
                    </div>
                )}

                <div className="mt-4 flex justify-end">
                    <Button
                        variant="ghost" size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={onDelete}
                    >
                        Delete Item
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
}
